import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { PageLoading } from '@invision/ui';
import type { Security_SessionSummary } from 'invApi';
import { useRetrieveSessionMutation } from '../../apis/useRetrieveSessionMutation';
import { useAuthenticationFlow } from '../../contexts/AuthenticationFlowContext';
import { usePostAuthRedirect } from '../../hooks';
import {
  AuthenticationStorage,
  clearAuthenticationApiSession,
  isCredentialUpdateRequired
} from '../../utils';
import { AUTHENTICATION_ROUTES } from '../../constants';

export function SessionGuard({ children }: PropsWithChildren) {
  const location = useLocation();
  const history = useHistory();

  const [isValidatingSession, setIsValidatingSession] = useState(false);
  const [sessionCheckComplete, setSessionCheckComplete] = useState(false);

  const { sessionId, systemId, redirectUrl, setRedirectUrl, clearState } =
    useAuthenticationFlow();
  const { mutateAsync: validateSession } = useRetrieveSessionMutation();
  const performPostAuthRedirect = usePostAuthRedirect();

  /*
   * Routes that must render without SessionGuard validating the session and
   * redirecting away based on it:
   *
   * 1. update-credentials: the user is mid-flow and must stay on the page.
   *
   * 2. logout: LogoutPage must be allowed to run to terminate the session.
   *    Validating here would find the still-valid session and redirect the user
   *    back into an application before logout executes, leaving them logged in.
   *
   * 3. access-redirect: the interstitial carries its destination in router
   *    state. Re-validating (with the redirect URL already cleared) would
   *    resolve the user's default module and silently complete the redirect,
   *    skipping the countdown and message the interstitial exists to show.
   *
   * Note: no-module-access is intentionally NOT exempt. Validating it means a
   * user who actually has module access lands on it (e.g. by typing the URL) is
   * redirected into their module, while a genuine no-access user is simply sent
   * back to the same page and stays.
   */
  const SESSION_VALIDATION_EXEMPT_ROUTES: string[] = [
    AUTHENTICATION_ROUTES.UPDATE_CREDENTIALS,
    AUTHENTICATION_ROUTES.LOGOUT,
    AUTHENTICATION_ROUTES.ACCESS_REDIRECT
  ];

  // Only validate session on non-exempt routes (where we might redirect away)
  const shouldValidateSession = !SESSION_VALIDATION_EXEMPT_ROUTES.includes(
    location.pathname
  );

  useEffect(() => {
    performSessionValidation();
  }, []);

  async function performSessionValidation() {
    if (!shouldValidateSession) {
      setSessionCheckComplete(true);
      return;
    }

    if (!sessionId || !systemId) {
      // A partial session (one id present without the other) is not a usable
      // session, but a lingering session id can still leak into unauthenticated
      // calls (e.g. RetrieveIdentityProvider). Purge it before showing the form.
      if (sessionId || systemId) {
        handleInvalidSession();
        return;
      }

      // No session data - proceed to show login/forgot password form
      setSessionCheckComplete(true);
      return;
    }

    setIsValidatingSession(true);

    try {
      const { SessionSummary } = await validateSession({});

      await handleValidSessionRedirect(SessionSummary);
    } catch (_error) {
      // Session is invalid - clear and proceed to show login form
      handleInvalidSession();
    } finally {
      setIsValidatingSession(false);
    }
  }

  async function handleValidSessionRedirect(
    sessionSummary?: Security_SessionSummary
  ) {
    if (isCredentialUpdateRequired(sessionSummary)) {
      history.replace(AUTHENTICATION_ROUTES.UPDATE_CREDENTIALS);
      // Destination renders under this guard, so stop showing the loader.
      setSessionCheckComplete(true);
      return;
    }

    const { navigatedWithinApp } = await performPostAuthRedirect({
      sessionSummary,
      systemId,
      storedRedirectUrl: redirectUrl,
      clearStoredRedirectUrl: () => setRedirectUrl(null),
      history,
      logContext: 'session-validation'
    });

    // When the redirect targets an auth-app route (e.g. the no-access page or
    // the access-redirect interstitial), that page renders beneath this guard,
    // so we must stop rendering the loading screen. For external module
    // redirects we keep the loader until the browser leaves the app.
    if (navigatedWithinApp) {
      setSessionCheckComplete(true);
    }
  }

  function handleInvalidSession() {
    clearState();
    AuthenticationStorage.clearAll();

    // Also drop the cached in-memory session so the invalid session cannot be
    // attached to a later unauthenticated request (e.g. RetrieveIdentityProvider).
    clearAuthenticationApiSession();

    // Proceed to show login/forgot password form
    setSessionCheckComplete(true);
  }

  // Show loading screen while validating session
  if (isValidatingSession) {
    return <PageLoading open />;
  }

  // Show loading if session check not complete (for redirects)
  if (!sessionCheckComplete) {
    return <PageLoading open />;
  }

  // Session validation complete - render the app
  return <>{children}</>;
}
